'use client';

// P4S (Page for Sale) — trang public, KHÔNG cần đăng nhập, tenant resolve theo subdomain (Host header,
// xem lib/sale.ts + lib/tenant-auth.ts). CHỈ hiện block/free + giá tuỳ chọn — KHÔNG bao giờ hiện
// booking/khách. Xem docs/booking-patterns.md + memory project_p4s_sale_portal_spec cho spec đầy đủ.
//
// Phase A-F đã xong (xem memory project_p4s_sale_portal_spec). Sale đăng nhập bằng Gmail (nút "Đăng
// nhập" dưới) → nếu chưa có tài khoản, tự vào hàng chờ duyệt của tenant (app/api/auth/sync-profile) —
// tenant admin duyệt tại /admin/users. Đã có role 'sale' → quota cao hơn tự áp dụng (Bearer token).

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBranding } from '@/lib/branding-context';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/Toast';
import Tip from '@/components/Tip';
import { dateAddDays, todayISO, fmtVND, fmtUSD, getOttChatUrl } from '@/lib/utils';
import { groupAmenities } from '@/lib/amenities';
import { logPublicClientError } from '@/lib/client-error-logger';
import { readPref, writePref } from '@/lib/prefs';
import { saleBookingEnabledFor } from '@/lib/sale-hold';
import SaleBookingPanel, { type SaleBookingInfo } from '@/components/sale/SaleBookingPanel';
import { readSaleGuest, saveSaleGuest } from '@/lib/sale-guest-info';
import { roomAlias } from '@/lib/sale-alias';
import SaleLangSwitcher from '@/components/sale/SaleLangSwitcher';
import { useSaleTr } from '@/lib/sale-i18n-dyn';
import { useSaleLang } from '@/lib/sale-i18n';

interface P4sMediaItem { id: string; type: 'photo' | 'video'; url: string }
interface P4sRoomType {
  id: string; code: string; name: string; description?: string;
  maxAdults: number; maxChildren: number; area?: number; bedrooms?: number;
  amenities: string[]; media: P4sMediaItem[];
  salePrice?: number; salePriceUnit?: 'night' | 'month' | 'sqm'; salePriceCurrency?: 'VND' | 'USD'; salePriceNote?: string;
}
interface P4sRoom { id: string; number: string; roomType: P4sRoomType | null; showPrice: boolean; isDuplex?: boolean }
type RoomTypeFilterKey = 'bed1' | 'bed2' | 'duplex' | 'office';
interface P4sBuilding { id: string; code: string; name: string; area: string; rooms: P4sRoom[] }
interface TlsCell { roomId: string; date: string; blocked: boolean; price?: number }

type SalePriceUnit = 'night' | 'month' | 'sqm';
type SalePriceCurrency = 'VND' | 'USD';

// Giá đêm (tham khảo short-stay) LUÔN báo bằng đ (khớp quy ước OTA). Giá tháng/m² (khách thuê dài
// hạn/văn phòng): mặc định báo bằng $ khi salePriceCurrency chưa set (quy ước Gardenia, yêu cầu
// 2026-08-21) — set salePriceCurrency='VND' cho tenant báo giá thật bằng đồng (vd Joohouse HDDH).
function fmtSalePriceAmount(price: number, currency?: SalePriceCurrency): string {
  return currency === 'VND' ? fmtVND(price) : fmtUSD(price);
}
type SaleT = (key: string, vars?: Record<string, string | number>) => string;
function formatSalePrice(price: number, unit: SalePriceUnit | undefined, currency: SalePriceCurrency | undefined, t: SaleT): string {
  if (unit === 'month') return `${fmtSalePriceAmount(price, currency)}${t('page.unit.month')}`;
  if (unit === 'sqm') return `${fmtSalePriceAmount(price, currency)}${t('page.unit.sqmMonth')}`;
  return `${fmtVND(price)}${t('page.unit.night')}`;
}
// Chuỗi dịch có thể chứa <strong>…</strong> / <b>…</b> (nhấn mạnh 1 cụm) — dựng thành phần tử, KHÔNG dùng innerHTML.
function renderBold(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /<(strong|b)>(.*?)<\/\1>/g;
  let last = 0, i = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(m[1] === 'b' ? <b key={i++} className="text-[#4a3d30]">{m[2]}</b> : <strong key={i++}>{m[2]}</strong>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
// Tổng tiền/tháng suy từ diện tích RoomType (chỉ áp dụng đơn vị m²) để Sale khỏi tự nhân — KHÔNG tính
// được nếu loại phòng chưa có field area.
function saleTotalMonthly(price: number, unit: SalePriceUnit | undefined, area: number | undefined): number | undefined {
  return unit === 'sqm' && area ? price * area : undefined;
}

const PRESETS = [
  { labelKey: 'page.preset.2w', days: 14 },
  { labelKey: 'page.preset.1m', days: 30 },
  { labelKey: 'page.preset.3m', days: 90 },
  { labelKey: 'page.preset.6m', days: 182 },
  { labelKey: 'page.preset.12m', days: 365 },
];

// Font riêng cho trang /sale (khác Inter mặc định toàn app) — chỉ nạp ở đây, không đụng globals.css.
const HEADING_FONT = { fontFamily: "'Be Vietnam Pro', 'Inter', sans-serif" };
const MONO_FONT = { fontFamily: "'IBM Plex Mono', ui-monospace, monospace" };

export default function P4sPage() {
  const { t, fdShort } = useSaleLang();
  const brand = useBranding();
  const { user, firebaseUser, error: authError, signInWithGoogle, signOut } = useAuth();
  const [buildings, setBuildings] = useState<P4sBuilding[] | null>(null);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [bookingInfo, setBookingInfo] = useState<SaleBookingInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [from, setFrom] = useState(todayISO());
  const [days, setDays] = useState(30);
  const [customMode, setCustomMode] = useState(false);
  const [customTo, setCustomTo] = useState(() => dateAddDays(todayISO(), 30));
  const to = useMemo(() => {
    if (!customMode) return dateAddDays(from, days);
    return customTo > from ? customTo : dateAddDays(from, 1);
  }, [customMode, customTo, from, days]);
  const [cells, setCells] = useState<TlsCell[] | null>(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [freeRoomIds, setFreeRoomIds] = useState<Set<string> | null>(null);
  const [buildingFilter, setBuildingFilter] = useState<string | null>(null);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [onlyFree, setOnlyFree] = useState(false);
  const [roomTypeFilter, setRoomTypeFilter] = useState<Set<RoomTypeFilterKey>>(new Set());
  const [detailRoomId, setDetailRoomId] = useState<string | null>(null);
  const [quota, setQuota] = useState<{ used: number; max: number } | null>(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [view, setView] = useState<'gallery' | 'timeline'>('gallery');
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Persist chip lọc (khu vực/toà/còn trống/view) theo user — Sale đã đăng nhập nhớ theo uid, ẩn danh
  // nhớ theo thiết bị (bucket 'anon'). KHÔNG persist khoảng ngày (from/to) — để mặc định "hôm nay" mỗi
  // lần vào lại, persist ngày cũ sẽ bị lệch/vô nghĩa khi quay lại sau nhiều ngày.
  useEffect(() => {
    const key = `gms:sale:filters:${user?.uid || 'anon'}`;
    try {
      const saved = readPref(key);
      if (saved) {
        const p = JSON.parse(saved) as { areaFilter?: string | null; buildingFilter?: string | null; onlyFree?: boolean; view?: 'gallery' | 'timeline'; roomTypeFilter?: string[] };
        setAreaFilter(p.areaFilter ?? null);
        setBuildingFilter(p.buildingFilter ?? null);
        setOnlyFree(!!p.onlyFree);
        if (p.view === 'gallery' || p.view === 'timeline') setView(p.view);
        const validKeys = new Set(['bed1', 'bed2', 'duplex']);
        setRoomTypeFilter(new Set((p.roomTypeFilter ?? []).filter(v => validKeys.has(v)) as RoomTypeFilterKey[]));
      }
    } catch { /* localStorage lỗi/hỏng dữ liệu — giữ mặc định */ }
    setFiltersLoaded(true);
  }, [user?.uid]);

  useEffect(() => {
    if (!filtersLoaded) return;
    const key = `gms:sale:filters:${user?.uid || 'anon'}`;
    writePref(key, JSON.stringify({ areaFilter, buildingFilter, onlyFree, view, roomTypeFilter: [...roomTypeFilter] }));
  }, [filtersLoaded, user?.uid, areaFilter, buildingFilter, onlyFree, view, roomTypeFilter]);

  const toggleRoomType = (v: RoomTypeFilterKey) => {
    setRoomTypeFilter(s => {
      const n = new Set(s);
      if (n.has(v)) { n.delete(v); } else { n.add(v); }
      return n;
    });
  };

  // Deep-link ?room=<id-hoặc-alias> — cho phép copy link mở thẳng modal chi tiết 1 căn (share vào nhóm
  // chat/khách xem). Đọc qua window.location (KHÔNG useSearchParams) để tránh phải wrap Suspense.
  // Nhận cả Firestore doc ID thật lẫn mã căn thân thiện {buildingCode}{number} (vd "HHT501") — khớp
  // alias cần `buildings` đã load nên để nguyên giá trị query, detailFromCatalog bên dưới tự khớp lại
  // khi buildings load xong (useMemo deps [detailRoomId, buildings]).
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('room');
    if (id) setDetailRoomId(id);
  }, []);
  const openRoom = (id: string) => {
    setDetailRoomId(id);
    // Đẩy URL bằng mã căn thân thiện nếu khớp được (buildings đã load) — fallback id thật nếu chưa.
    let alias = id;
    for (const b of buildings ?? []) {
      const r = b.rooms.find(rr => rr.id === id);
      if (r) { alias = roomAlias(b.code, r.number); break; }
    }
    window.history.pushState(null, '', `?room=${alias}`);
  };
  const closeRoom = () => {
    setDetailRoomId(null);
    window.history.pushState(null, '', window.location.pathname);
  };

  // Chuyển cả khoảng ngày (giữ nguyên độ dài) lùi/tiến 1 tuần — không lùi quá 7 ngày trước hôm nay
  // (Sale bán phòng trong tương lai, không cần xem lịch trống quá khứ xa → tránh lộ thông tin thừa).
  // Khớp SALE_MIN_FROM_PAST_DAYS ở lib/sale.ts (API cũng chặn — không import trực tiếp vì lib/sale.ts
  // kéo firebase-admin, phá client bundle).
  const minFrom = useMemo(() => dateAddDays(todayISO(), -7), []);
  const shiftWeek = (deltaWeeks: number) => {
    const shifted = dateAddDays(from, deltaWeeks * 7);
    let nextFrom = shifted;
    if (nextFrom < minFrom) nextFrom = minFrom;
    setFrom(nextFrom);
    if (customMode) {
      const durationDays = Math.round((new Date(customTo).getTime() - new Date(from).getTime()) / 86_400_000);
      setCustomTo(dateAddDays(nextFrom, durationDays));
    }
  };
  const goToday = () => {
    const today = todayISO();
    if (customMode) {
      const durationDays = Math.round((new Date(customTo).getTime() - new Date(from).getTime()) / 86_400_000);
      setCustomTo(dateAddDays(today, durationDays));
    }
    setFrom(today);
  };

  useEffect(() => {
    fetch('/api/sale/catalog')
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(d => { setBuildings(d.buildings ?? []); setContactPhone(d.contactPhone ?? null); setBookingInfo(d.booking ?? null); })
      .catch(() => setLoadError(true));
  }, []);

  // Chống race khi lùi/tiến tuần liên tục (shiftWeek/goToday BẤM ĐƯỢC LIÊN TỤC, chủ đích cho phép) —
  // cùng bug đã vá ở /pricing (xem memory feedback_stale_async_filter_race): nếu response của tuần CŨ
  // về SAU response tuần MỚI, setCells/setFreeRoomIds sẽ ghi đè bằng lịch của tuần đã rời. Chỉ gọi từ
  // đúng 1 effect này → dùng `cancelled` qua cleanup, không cần reqId ref.
  useEffect(() => {
    let cancelled = false;
    setGridLoading(true);
    (firebaseUser ? firebaseUser.getIdToken() : Promise.resolve(null)).then(token => {
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      return Promise.all([
        fetch(`/api/sale/timeline?from=${from}&to=${to}`).then(r => r.json()),
        fetch(`/api/sale/search?from=${from}&to=${to}`, { headers: authHeaders }).then(r => r.json().then(body => ({ status: r.status, body }))),
      ]);
    })
      .then(([tl, sr]) => {
        if (cancelled) return;
        setCells(tl.cells ?? []);
        if (sr.status === 429) {
          setQuotaBlocked(true);
          setQuota(sr.body.quota ?? null);
          // KHÔNG set new Set() (rỗng) — đó có nghĩa "đã kiểm tra, không có phòng nào trống", còn
          // hết lượt là "CHƯA kiểm tra được". Để null (chưa biết) — bảng /timeline vẫn đúng vì
          // route đó không giới hạn lượt, chỉ badge/chip "Còn trống" cần biết mình đang thiếu dữ liệu.
          setFreeRoomIds(null);
          return;
        }
        setQuotaBlocked(false);
        setFreeRoomIds(new Set<string>(sr.body.roomIds ?? []));
        if (sr.body.quota) setQuota(sr.body.quota);
      })
      .catch(err => {
        if (cancelled) return;
        logPublicClientError({ route: 'app/sale/page.tsx', action: 'load_timeline_search', message: err instanceof Error ? err.message : String(err), details: { from, to } });
        setCells([]); setFreeRoomIds(null);
      })
      .finally(() => { if (!cancelled) setGridLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, firebaseUser]);

  const quotaWarn = quota && !quotaBlocked && quota.used / quota.max >= 0.5;

  const cellsByRoom = useMemo(() => {
    const m = new Map<string, TlsCell[]>();
    (cells ?? []).forEach(c => {
      const arr = m.get(c.roomId) ?? [];
      arr.push(c);
      m.set(c.roomId, arr);
    });
    return m;
  }, [cells]);

  const areas = useMemo(() => {
    if (!buildings) return [];
    const counts = new Map<string, number>();
    for (const b of buildings) counts.set(b.area, (counts.get(b.area) ?? 0) + b.rooms.length);
    return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [buildings]);

  const visibleBuildings = useMemo(() => {
    if (!buildings) return [];
    return buildings
      .filter(b => !areaFilter || b.area === areaFilter)
      .filter(b => !buildingFilter || b.id === buildingFilter);
  }, [buildings, buildingFilter, areaFilter]);

  // Đếm theo từng loại (1 ngủ/2 ngủ/duplex) trong phạm vi khu vực/toà đang lọc — KHÔNG trừ theo
  // roomTypeFilter của chính nó (chuẩn hành vi chip: số đếm là "nếu bấm chip này sẽ ra bao nhiêu",
  // không tự trừ dần khi đã chọn chip khác cùng nhóm).
  const roomTypeCounts = useMemo(() => {
    const rooms = visibleBuildings.flatMap(b => b.rooms);
    return {
      bed1: rooms.filter(r => r.roomType?.bedrooms === 1).length,
      bed2: rooms.filter(r => r.roomType?.bedrooms === 2).length,
      duplex: rooms.filter(r => r.isDuplex).length,
      office: rooms.filter(r => r.roomType?.code === 'VP').length,
    };
  }, [visibleBuildings]);

  // Bug cùng lớp PerformanceTab (2026-09-13): roomTypeFilter là bộ lọc RIÊNG so với khu vực/toà — đổi
  // khu vực/toà không tự dọn lựa chọn loại phòng cũ. Chip "Văn phòng" chỉ hiện khi roomTypeCounts.office
  // > 0 (xem gate bên dưới) — nếu chọn "Văn phòng" ở toà A rồi chuyển sang toà B không có căn VP, chip
  // đó biến mất NHƯNG roomTypeFilter vẫn còn 'office' → matchesRoomTypeFilter luôn false → danh sách
  // rỗng mà không còn cách nào qua UI để bỏ chọn. Tự dọn về đúng loại phòng còn tồn tại trong phạm vi
  // khu vực/toà đang chọn.
  useEffect(() => {
    if (roomTypeFilter.size === 0) return;
    setRoomTypeFilter(s => {
      const n = new Set([...s].filter(k => roomTypeCounts[k] > 0));
      return n.size === s.size ? s : n;
    });
  }, [roomTypeCounts]);

  const matchesRoomTypeFilter = (room: P4sRoom) => {
    if (roomTypeFilter.size === 0) return true;
    return (
      (roomTypeFilter.has('bed1') && room.roomType?.bedrooms === 1) ||
      (roomTypeFilter.has('bed2') && room.roomType?.bedrooms === 2) ||
      (roomTypeFilter.has('duplex') && !!room.isDuplex) ||
      (roomTypeFilter.has('office') && room.roomType?.code === 'VP')
    );
  };

  // Toà + khu vực (visibleBuildings) sau khi lọc thêm theo loại phòng — dùng cho danh sách phòng/lịch
  // trống thực tế hiển thị (KHÔNG dùng để đếm số chip Khu vực/Toà — 2 chip đó vẫn hiện tổng tồn kho).
  const filteredBuildings = useMemo(
    () => visibleBuildings.map(b => ({ ...b, rooms: b.rooms.filter(matchesRoomTypeFilter) })),
    [visibleBuildings, roomTypeFilter],
  );

  // Số căn còn trống SUỐT khoảng ngày đang chọn, trong đúng phạm vi khu vực/toà/loại phòng đang lọc —
  // dùng cho chip "Còn trống (X)". freeRoomIds đến từ /api/sale/search (đã tính theo from/to hiện tại).
  const freeCountInView = useMemo(
    () => filteredBuildings.flatMap(b => b.rooms).filter(r => freeRoomIds?.has(r.id)).length,
    [filteredBuildings, freeRoomIds],
  );
  let freeCountLabel = String(freeCountInView);
  if (gridLoading) freeCountLabel = '…';
  else if (quotaBlocked) freeCountLabel = '—';

  // Dữ liệu chi tiết phòng ĐÃ CÓ SẴN trong `buildings` (catalog load lúc vào trang) — truyền thẳng
  // xuống modal thay vì bắt modal tự gọi lại /api/sale/rooms/[roomId], đỡ 1 round-trip mỗi lần bấm
  // xem 1 phòng (đây là điểm chậm rõ nhất trên /sale). Modal chỉ tự fetch khi KHÔNG tìm thấy (vd mở
  // link ?room=<id> trước khi catalog load xong, hoặc phòng đã bị ẩn khỏi catalog).
  const detailFromCatalog = useMemo(() => {
    if (!detailRoomId || !buildings) return undefined;
    for (const b of buildings) {
      const r = b.rooms.find(rr => rr.id === detailRoomId || roomAlias(b.code, rr.number) === detailRoomId);
      if (r) return { id: r.id, number: r.number, buildingName: b.name, buildingCode: b.code, roomType: r.roomType, showPrice: r.showPrice, isDuplex: r.isDuplex };
    }
    return undefined;
  }, [detailRoomId, buildings]);

  return (
    <div className="min-h-screen bg-[#f8f5f1] text-[#1c1a16]">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@700;800&family=IBM+Plex+Mono:wght@500;600&display=swap" />
      <header className="border-b border-[#e4ddd4] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Ưu tiên logoMarkUrl (mark vuông 256×256) cho khung vuông này — logoUrl (logo đầy đủ,
              thường không vuông) sẽ bị BÓP MÉO nếu ép vào w-8 h-8 mà không object-contain. */}
          {(brand?.logoMarkUrl || brand?.logoUrl) && (
            <img src={brand.logoMarkUrl || brand.logoUrl || ''} alt="" className="w-8 h-8 rounded-lg object-contain" />
          )}
          <div className="flex-1">
            <div className="font-bold text-[16px]" style={HEADING_FONT}>{brand?.name || t('page.header.title')}</div>
            <div className="text-[11px] text-[#8a7a68]">{t('page.header.subtitle')}</div>
          </div>
          <SaleLangSwitcher />
          {user?.role === 'sale' ? (
            <div className="flex items-center gap-2 text-xs text-[#4a3d30]">
              <span>{user.displayName || user.email}</span>
              <button onClick={() => signOut()} className="text-[#8a7a68] hover:underline">{t('page.header.signOut')}</button>
            </div>
          ) : (
            <button onClick={() => signInWithGoogle()} className="px-3 py-1.5 rounded-lg border border-[#e4ddd4] text-xs font-medium text-[#4a3d30] hover:bg-[#f0ece5]">
              {t('page.header.signIn')}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {authError && (
          <div className="p-3 rounded-xl bg-blue-50 text-blue-700 text-sm">{authError}</div>
        )}
        {quotaBlocked && (
          <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm space-y-2">
            <div>
              {renderBold(t('page.quota.exhausted', { max: quota?.max ?? '—' }))}
            </div>
            {user?.role !== 'sale' && (
              <button onClick={() => signInWithGoogle()}
                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold">
                {t('page.quota.signInGoogle')}
              </button>
            )}
          </div>
        )}
        {quotaWarn && !quotaBlocked && (
          <div className="p-3 rounded-xl bg-amber-50 text-amber-700 text-xs">
            {t('page.quota.warn', { used: quota!.used, max: quota!.max })}
          </div>
        )}
        {loadError && (
          <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm">
            {t('page.loadError')}
          </div>
        )}

        {buildings === null && !loadError && (
          <div className="text-sm text-[#8a7a68]">{t('page.loading')}</div>
        )}

        {buildings !== null && buildings.length === 0 && (
          <div className="p-6 rounded-xl bg-white border border-[#e4ddd4] text-sm text-[#8a7a68] text-center">
            {t('page.empty.catalog')}
          </div>
        )}

        {buildings !== null && buildings.length > 0 && (
          <>
            {/* Bộ lọc khu vực + toà + tìm ngày trống */}
            <div className="bg-white rounded-2xl border border-[#e4ddd4] p-4 space-y-3">
              {areas.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-[#8a7a68] self-center mr-1">{t('page.filter.area')}</span>
                  <button onClick={() => { setAreaFilter(null); setBuildingFilter(null); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${areaFilter === null ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                    {t('page.filter.all')}
                  </button>
                  {areas.map(a => (
                    <button key={a.name} onClick={() => { setAreaFilter(a.name); setBuildingFilter(null); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${areaFilter === a.name ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                      {a.name} ({a.count})
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {buildings.length > 1 && (
                  <>
                    <button onClick={() => setBuildingFilter(null)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${buildingFilter === null ? 'bg-[#1c1a16] text-white border-[#1c1a16]' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                      {t('page.filter.allBuildings', { n: visibleBuildings.reduce((n, b) => n + b.rooms.length, 0) })}
                    </button>
                    {buildings.filter(b => !areaFilter || b.area === areaFilter).map(b => (
                      <button key={b.id} onClick={() => setBuildingFilter(b.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${buildingFilter === b.id ? 'bg-[#1c1a16] text-white border-[#1c1a16]' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                        {b.name} ({b.rooms.length})
                      </button>
                    ))}
                  </>
                )}
                <Tip text={quotaBlocked ? t('page.filter.freeBlocked') : ''}>
                  <button onClick={() => setOnlyFree(v => !v)} disabled={gridLoading || quotaBlocked}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50 ${onlyFree ? 'bg-emerald-600 text-white border-emerald-600' : 'border-emerald-300 text-emerald-700'}`}>
                    {t('page.filter.free', { n: freeCountLabel })}
                  </button>
                </Tip>
              </div>
              {(roomTypeCounts.bed1 + roomTypeCounts.bed2 + roomTypeCounts.duplex + roomTypeCounts.office) > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-[#8a7a68] self-center mr-1">{t('page.filter.roomType')}</span>
                  <button onClick={() => toggleRoomType('bed1')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${roomTypeFilter.has('bed1') ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                    {t('page.filter.bed1', { n: roomTypeCounts.bed1 })}
                  </button>
                  <button onClick={() => toggleRoomType('bed2')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${roomTypeFilter.has('bed2') ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                    {t('page.filter.bed2', { n: roomTypeCounts.bed2 })}
                  </button>
                  <button onClick={() => toggleRoomType('duplex')}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${roomTypeFilter.has('duplex') ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                    {t('page.filter.duplex', { n: roomTypeCounts.duplex })}
                  </button>
                  {roomTypeCounts.office > 0 && (
                    <button onClick={() => toggleRoomType('office')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${roomTypeFilter.has('office') ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                      {t('page.filter.office', { n: roomTypeCounts.office })}
                    </button>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#e4ddd4]">
                <span className="text-xs text-[#8a7a68] mr-1">{t('page.date.viewFrom')}</span>
                <Tip text={t('page.date.prevWeek')}>
                  <button onClick={() => shiftWeek(-1)} disabled={from <= minFrom}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#e4ddd4] text-[#4a3d30] disabled:opacity-30">
                    ‹
                  </button>
                </Tip>
                <input type="date" value={from} min={minFrom}
                  onChange={e => { const v = e.target.value; setFrom(v < minFrom ? minFrom : v); }}
                  className="border border-[#e4ddd4] rounded-lg px-2 py-1 text-sm" />
                <Tip text={t('page.date.nextWeek')}>
                  <button onClick={() => shiftWeek(1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#e4ddd4] text-[#4a3d30]">
                    ›
                  </button>
                </Tip>
                <button onClick={goToday} disabled={from === todayISO()}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium border border-[#e4ddd4] text-[#4a3d30] disabled:opacity-30">
                  {t('page.date.today')}
                </button>
                <span className="text-xs text-[#8a7a68]">{t('page.date.within')}</span>
                {PRESETS.map(p => (
                  <button key={p.days} onClick={() => { setCustomMode(false); setDays(p.days); }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${!customMode && days === p.days ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                    {t(p.labelKey)}
                  </button>
                ))}
                <button onClick={() => { setCustomMode(true); if (customTo <= from) setCustomTo(dateAddDays(from, 30)); }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${customMode ? 'bg-brand-600 text-white border-brand-600' : 'border-[#e4ddd4] text-[#4a3d30]'}`}>
                  {t('page.date.custom')}
                </button>
                {customMode && (
                  <>
                    <span className="text-xs text-[#8a7a68]">{t('page.date.until')}</span>
                    <input type="date" value={to} min={dateAddDays(from, 1)} onChange={e => setCustomTo(e.target.value)}
                      className="border border-[#e4ddd4] rounded-lg px-2 py-1 text-sm" />
                  </>
                )}
                <div className="ml-auto flex gap-1 bg-[#f0ece5] rounded-lg p-0.5">
                  <button onClick={() => setView('gallery')}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold ${view === 'gallery' ? 'bg-white shadow-sm text-[#1c1a16]' : 'text-[#8a7a68]'}`}>
                    {t('page.view.gallery')}
                  </button>
                  <button onClick={() => setView('timeline')}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold ${view === 'timeline' ? 'bg-white shadow-sm text-[#1c1a16]' : 'text-[#8a7a68]'}`}>
                    {t('page.view.timeline')}
                  </button>
                </div>
              </div>
            </div>

            {view === 'gallery' && (
              <RoomGallery
                buildings={filteredBuildings}
                freeRoomIds={freeRoomIds}
                onlyFree={onlyFree}
                loading={gridLoading}
                onOpenRoom={openRoom}
              />
            )}

            {/* TLS grid */}
            {view === 'timeline' && <div className="bg-white rounded-2xl border border-[#e4ddd4] overflow-x-auto">
              {gridLoading ? (
                <div className="p-6 text-sm text-[#8a7a68]">{t('page.tl.loading')}</div>
              ) : (
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-white text-left px-3 py-2 border-b border-[#e4ddd4] font-semibold">{t('page.tl.room')}</th>
                      {(cellsByRoom.size > 0 ? (cellsByRoom.values().next().value ?? []) : []).map(c => (
                        <th key={c.date} className="px-1.5 py-2 border-b border-[#e4ddd4] font-normal text-[#8a7a68] whitespace-nowrap">
                          {fdShort(c.date)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBuildings.flatMap(b => b.rooms.map(r => ({ ...r, buildingCode: b.code })))
                      .filter(room => !onlyFree || freeRoomIds?.has(room.id))
                      .map(room => {
                      const roomCells = cellsByRoom.get(room.id) ?? [];
                      const free = freeRoomIds?.has(room.id);
                      return (
                        <tr key={room.id} className="border-b border-[#f0ece5] last:border-0">
                          <td className="sticky left-0 bg-white px-3 py-1.5 font-medium whitespace-nowrap">
                            <button onClick={() => openRoom(room.id)} className="hover:underline text-left">
                              <span className="text-[10px] text-[#8a7a68] font-normal">{room.buildingCode}</span> {room.number}
                            </button>
                            {gridLoading ? (
                              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-[#f0ece5] text-[#8a7a68] text-[10px] font-semibold">{t('page.tl.loadingBadge')}</span>
                            ) : free && (
                              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-semibold">{t('page.tl.allFree')}</span>
                            )}
                          </td>
                          {roomCells.map(c => (
                            <td key={c.date} className="px-1.5 py-1.5 text-center">
                              <Tip block dir="down" text={c.blocked ? t('page.tl.cellBooked') : (c.price ? fmtVND(c.price) : t('page.tl.cellFree'))}>
                                <div className={`h-4 rounded ${c.blocked ? 'bg-[#c96a5e]' : 'bg-emerald-200'}`} />
                              </Tip>
                              {!c.blocked && c.price != null && (
                                <div className="text-[9px] text-[#8a7a68] mt-0.5 whitespace-nowrap">{Math.round(c.price / 1000)}k</div>
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>}
            {view === 'timeline' && (
              <p className="text-[11px] text-[#8a7a68]">
                {t('page.tl.legend')}
              </p>
            )}
          </>
        )}
      </main>

      {detailRoomId && (
        <RoomDetailModal roomId={detailRoomId} preloaded={detailFromCatalog} booking={bookingInfo} contactPhone={contactPhone} searchRange={customMode ? { from, to } : null} onClose={closeRoom} />
      )}
      <SaleChatWidget contactPhone={contactPhone} onOpenRoom={openRoom} />
    </div>
  );
}

function RoomGallery({
  buildings, freeRoomIds, onlyFree, loading, onOpenRoom,
}: Readonly<{
  buildings: P4sBuilding[]; freeRoomIds: Set<string> | null; onlyFree: boolean; loading: boolean; onOpenRoom: (id: string) => void;
}>) {
  const { t } = useSaleLang();
  const allRooms = buildings.flatMap(b => b.rooms.map(r => ({ ...r, buildingName: b.name })));
  const rooms = onlyFree ? allRooms.filter(r => freeRoomIds?.has(r.id)) : allRooms;
  const tr = useSaleTr(rooms.map(r => r.roomType?.name)); // tên loại phòng = dữ liệu tenant → dịch động (cache Firestore)
  const freeCount = freeRoomIds ? allRooms.filter(r => freeRoomIds.has(r.id)).length : null;

  // Các căn CÙNG loại phòng (roomType) hay có bộ ảnh mở đầu na ná nhau (cùng concept decor) — nếu
  // luôn lấy photos[0] thì cả dải card nhìn y hệt. Lệch dần chỉ số ảnh theo thứ tự trong nhóm cùng
  // roomType để mỗi card trong nhóm hiện ảnh khác nhau, KHÔNG đụng tới thứ tự ảnh thật trong modal.
  const thumbIndexByRoomTypeKey = new Map<string, number>();
  const thumbnailOf = (room: (typeof rooms)[number]): string | undefined => {
    // Cover = ẢNH đầu tiên trong album (video không dùng làm thumbnail tĩnh được).
    const photos = (room.roomType?.media ?? []).filter(m => m.type === 'photo').map(m => m.url);
    if (photos.length === 0) return undefined;
    const key = room.roomType?.id ?? room.id;
    const n = thumbIndexByRoomTypeKey.get(key) ?? 0;
    thumbIndexByRoomTypeKey.set(key, n + 1);
    return photos[n % photos.length];
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-[#8a7a68]">
        <span style={MONO_FONT} className="font-semibold text-[#4a3d30]">{rooms.length}</span> {t('page.gallery.count')}
        {freeCount !== null && (
          <> · <span style={MONO_FONT} className="font-semibold text-emerald-600">{freeCount}</span> {t('page.gallery.freeAcross')}</>
        )}
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
        {rooms.map(room => {
          const free = freeRoomIds?.has(room.id) ?? null;
          const photo = thumbnailOf(room);
          const amenities = room.roomType?.amenities ?? [];
          const videoCount = (room.roomType?.media ?? []).filter(m => m.type === 'video').length;
          const isOffice = room.roomType?.code === 'VP';
          return (
            <button key={room.id} type="button" onClick={() => onOpenRoom(room.id)}
              className={`text-left bg-white rounded-2xl border overflow-hidden hover:shadow-md transition-all ${isOffice ? 'border-slate-300 hover:border-slate-400' : 'border-[#e4ddd4] hover:border-[#d8cfc2]'}`}>
              <div className={`aspect-16/10 relative ${isOffice ? 'bg-slate-100' : 'bg-[#f0ece5]'} text-gray-800`}>
                {photo
                  ? // eslint-disable-next-line @next/next/no-img-element
                    <img src={photo} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <div className={`w-full h-full flex items-center justify-center text-3xl ${isOffice ? 'text-slate-300' : 'text-[#c7c1b8]'}`}>{isOffice ? '🏢' : '🏠'}</div>}
                {isOffice && (
                  <span className="absolute top-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-700 text-white">{t('page.gallery.office')}</span>
                )}
                {videoCount > 0 && (
                  <span className="absolute bottom-1.5 right-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-black/60 text-white">▶ {videoCount}</span>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-bold text-[15px]" style={HEADING_FONT}>{room.buildingName} · {room.number}</div>
                  {loading ? (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-[#f0ece5] text-[#8a7a68]">{t('page.tl.loadingBadge')}</span>
                  ) : free !== null && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${free ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                      {free ? t('page.gallery.free') : t('page.gallery.booked')}
                    </span>
                  )}
                </div>
                {room.roomType && (
                  <div className="text-xs text-[#8a7a68] flex items-center gap-1.5">
                    <span>
                      {tr(room.roomType.name)} · 👤 {t('page.gallery.guests', { n: room.roomType.maxAdults })}
                      {room.roomType.area ? ` · 📐 ${room.roomType.area}m²` : ''}
                    </span>
                    {room.isDuplex && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">Duplex</span>
                    )}
                  </div>
                )}
                {room.roomType?.salePrice != null && (
                  <div className="text-[13px] font-bold text-brand-700">
                    {formatSalePrice(room.roomType.salePrice, room.roomType.salePriceUnit, room.roomType.salePriceCurrency, t)}<span className="font-medium text-[#8a7a68]">{t('page.gallery.refPrice')}</span>
                    {(() => {
                      const total = saleTotalMonthly(room.roomType.salePrice, room.roomType.salePriceUnit, room.roomType.area);
                      return total != null ? <span className="font-medium text-[#8a7a68]"> · ≈ {fmtSalePriceAmount(total, room.roomType.salePriceCurrency)}{t('page.unit.month')}</span> : null;
                    })()}
                  </div>
                )}
                {amenities.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {amenities.slice(0, 3).map(a => (
                      <span key={a} className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0ece5] text-[#4a3d30]">{a}</span>
                    ))}
                    {amenities.length > 3 && <span className="text-[10px] px-1 py-0.5 text-[#8a7a68]">+{amenities.length - 3}</span>}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {loading && <div className="text-xs text-[#8a7a68]">{t('page.gallery.updating')}</div>}
      {rooms.length === 0 && <div className="p-6 rounded-xl bg-white border border-[#e4ddd4] text-sm text-[#8a7a68] text-center">{t('page.gallery.noMatch')}</div>}
    </div>
  );
}

interface RoomRef { roomId: string; number: string; buildingCode: string }
interface ChatMsg { role: 'user' | 'assistant'; text: string; roomRefs?: RoomRef[] }

// Câu hỏi mẫu — vừa cho khách bấm hỏi ngay, vừa gợi ý phạm vi AI trả lời được (mô tả/tiện ích/trống-có khách).
const SAMPLE_QUESTION_KEYS = ['page.chat.sample1', 'page.chat.sample2', 'page.chat.sample3', 'page.chat.sample4'];

// Format tối giản cho câu trả lời AI: đoạn cách nhau (\n\n), gạch đầu dòng "- " thành <ul>, **đậm**/*nghiêng*.
// KHÔNG dùng thư viện markdown ngoài — chỉ 3 cú pháp model được dặn dùng trong prompt (route.ts), viết tay đủ nhẹ.
function formatInlineMd(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0, i = 0, m: RegExpExecArray | null;
  while ((m = regex.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(m[1] !== undefined ? <strong key={`${keyPrefix}-${i++}`}>{m[1]}</strong> : <em key={`${keyPrefix}-${i++}`}>{m[2]}</em>);
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function ChatText({ text }: Readonly<{ text: string }>) {
  const blocks = text.split(/\n{2,}/).map(b => b.trim()).filter(Boolean);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length > 0 && lines.every(l => l.startsWith('- '))) {
          return (
            <ul key={bi} className={`list-disc pl-4 space-y-0.5 ${bi > 0 ? 'mt-2' : ''}`}>
              {lines.map((l, li) => <li key={li}>{formatInlineMd(l.slice(2), `${bi}-${li}`)}</li>)}
            </ul>
          );
        }
        return (
          <p key={bi} className={bi > 0 ? 'mt-2' : undefined}>
            {lines.map((l, li) => (
              <span key={li}>
                {li > 0 && <br />}
                {formatInlineMd(l, `${bi}-${li}`)}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

function SaleChatWidget({ contactPhone, onOpenRoom }: Readonly<{ contactPhone: string | null; onOpenRoom: (roomId: string) => void }>) {
  const { t, lang } = useSaleLang();
  const { firebaseUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const send = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMsgs(p => [...p, { role: 'user', text }]);
    setSending(true);
    try {
      const token = firebaseUser ? await firebaseUser.getIdToken() : null;
      const res = await fetch('/api/sale/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: text, lang }),
      });
      const body = await res.json();
      if (res.status === 429) {
        setBlocked(true);
        setMsgs(p => [...p, { role: 'assistant', text: t('page.chat.limit') }]);
      } else if (!res.ok) {
        setMsgs(p => [...p, { role: 'assistant', text: t('page.chat.error') }]);
      } else {
        setMsgs(p => [...p, { role: 'assistant', text: body.reply, roomRefs: body.roomRefs }]);
      }
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_chat', message: e instanceof Error ? e.message : String(e) });
      setMsgs(p => [...p, { role: 'assistant', text: t('page.chat.offline') }]);
    }
    setSending(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 w-14 h-14 rounded-full bg-brand-600 text-white shadow-lg flex items-center justify-center text-xl">
        💬
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 max-h-[70vh] bg-white rounded-2xl border border-[#e4ddd4] shadow-xl flex flex-col overflow-hidden z-40">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e4ddd4]">
        <div className="font-semibold text-sm">{t('page.chat.title')}</div>
        <button onClick={() => setOpen(false)} className="text-[#8a7a68] text-sm">✕</button>
      </div>
      {contactPhone && (
        <a href={getOttChatUrl('zalo', contactPhone)} target="_blank" rel="noreferrer"
          className="block px-4 py-1.5 text-xs text-center text-brand-600 bg-[#f8f5f1] border-b border-[#e4ddd4] hover:underline">
          {t('page.chat.zalo')}
        </a>
      )}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
        {msgs.length === 0 && (
          <div className="space-y-2">
            <div className="text-[#8a7a68] text-xs">{t('page.chat.intro')}</div>
            <div className="flex flex-wrap gap-1.5">
              {SAMPLE_QUESTION_KEYS.map(k => (
                <button key={k} onClick={() => send(t(k))} disabled={sending || blocked}
                  className="px-2.5 py-1 rounded-full border border-[#e4ddd4] bg-[#f8f5f1] text-[#4a3d30] text-xs hover:bg-[#f0ece5] disabled:opacity-50">
                  {t(k)}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`px-3 py-2 rounded-xl max-w-[85%] ${m.role === 'user' ? 'bg-brand-600 text-white ml-auto' : 'bg-[#f0ece5] text-[#1c1a16]'}`}>
            {m.role === 'assistant' ? <ChatText text={m.text} /> : m.text}
            {!!m.roomRefs?.length && (
              <div className="flex flex-wrap gap-1 mt-2">
                {m.roomRefs.map(r => (
                  <button key={r.roomId} onClick={() => onOpenRoom(r.roomId)}
                    className="px-2 py-0.5 rounded-full bg-white border border-brand-600 text-brand-600 text-xs hover:bg-brand-50">
                    {t('page.chat.view', { code: `${r.buildingCode}${r.number}` })}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && <div className="text-[#8a7a68] text-xs">{t('page.chat.answering')}</div>}
      </div>
      <div className="p-2 border-t border-[#e4ddd4] flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          disabled={blocked} placeholder={t('page.chat.placeholder')} className="flex-1 border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm disabled:opacity-50" />
        <button onClick={() => send()} disabled={sending || blocked} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm disabled:opacity-50">{t('page.chat.send')}</button>
      </div>
    </div>
  );
}

interface RoomDetail {
  id: string; number: string; buildingName: string; buildingCode: string;
  roomType: P4sRoomType | null; showPrice: boolean; isDuplex?: boolean;
}

function RoomDetailModal({ roomId, preloaded, booking, contactPhone, searchRange, onClose }: Readonly<{ roomId: string; preloaded?: RoomDetail; booking: SaleBookingInfo | null; contactPhone: string | null; searchRange: { from: string; to: string } | null; onClose: () => void }>) {
  const { t } = useSaleLang();
  const { user, firebaseUser } = useAuth();
  const { toast } = useToast();
  const [detail, setDetail] = useState<RoomDetail | null>(preloaded ?? null);
  const [error, setError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  const copyLink = () => {
    // roomId prop có thể là alias thô chưa resolve (mở từ link ?room=<alias> trước khi detail load) —
    // ưu tiên alias tính lại từ `detail` đã resolve (buildingCode+number thật), fallback roomId gốc.
    const slug = detail ? roomAlias(detail.buildingCode, detail.number) : roomId;
    const link = `${window.location.origin}/sale?room=${slug}`;
    navigator.clipboard.writeText(link).then(
      () => toast(t('page.detail.copied')),
      () => toast(t('page.detail.copyFail'), 'error'),
    );
  };

  const downloadPhoto = async (photoUrl: string, index: number) => {
    if (downloading) return;
    if (!firebaseUser || user?.role !== 'sale') {
      toast(t('page.detail.needSale'), 'error');
      return;
    }
    setDownloading(true);
    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`/api/sale/download-photo?roomId=${detail?.id ?? roomId}&url=${encodeURIComponent(photoUrl)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast(t('page.detail.dlFail'), 'error'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${detail?.buildingCode ?? ''}${detail?.number ?? ''}-${index + 1}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'download_photo', message: e instanceof Error ? e.message : String(e) });
      toast(t('page.detail.dlFailNet'), 'error');
    }
    setDownloading(false);
  };

  useEffect(() => {
    setError(false);
    setLightboxIndex(null);
    if (preloaded) { setDetail(preloaded); return; } // đã có sẵn từ catalog — khỏi gọi API
    setDetail(null);
    fetch(`/api/sale/rooms/${roomId}`)
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(d => setDetail(d.room))
      .catch(() => setError(true));
  }, [roomId, preloaded]);

  const media = detail?.roomType?.media ?? [];
  const amenityGroups = useMemo(() => groupAmenities(detail?.roomType?.amenities ?? [], { sortByValue: true }), [detail?.roomType?.amenities]);
  const tr = useSaleTr([detail?.roomType?.name, detail?.roomType?.salePriceNote]);

  useEffect(() => {
    if (lightboxIndex === null || media.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowLeft') setLightboxIndex(i => (i === null ? null : (i - 1 + media.length) % media.length));
      else if (e.key === 'ArrowRight') setLightboxIndex(i => (i === null ? null : (i + 1) % media.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, media.length]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-5 [scrollbar-width:thin] [scrollbar-color:#d8cfc2_transparent] [scrollbar-gutter:auto]" onClick={e => e.stopPropagation()}>
        {error && <div className="text-sm text-rose-600">{t('page.detail.loadError')}</div>}
        {!error && !detail && <div className="text-sm text-[#8a7a68]">{t('page.loading')}</div>}
        {detail && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-lg">{t('page.detail.title', { number: detail.number, building: detail.buildingName })}</div>
                <div className="flex items-center gap-1.5">
                  {detail.roomType && <div className="text-sm text-[#8a7a68]">{tr(detail.roomType.name)}</div>}
                  {detail.isDuplex && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Duplex</span>
                  )}
                </div>
              </div>
              <Tip text={t('page.detail.copyTip')}>
                <button onClick={copyLink}
                  className="flex-none flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e4ddd4] text-xs font-medium text-[#4a3d30] hover:bg-[#f0ece5]">
                  {t('page.detail.share')}
                </button>
              </Tip>
            </div>
            {detail.roomType?.salePrice != null && (
              <div className="flex items-baseline gap-1.5 px-3 py-2 rounded-xl bg-amber-50 flex-wrap text-gray-800">
                <span className="text-lg font-bold text-amber-800">{formatSalePrice(detail.roomType.salePrice, detail.roomType.salePriceUnit, detail.roomType.salePriceCurrency, t)}</span>
                <span className="text-xs text-amber-700">{t('page.detail.salePriceLabel')}</span>
                {(() => {
                  const total = saleTotalMonthly(detail.roomType.salePrice, detail.roomType.salePriceUnit, detail.roomType.area);
                  return total != null ? <span className="text-xs text-amber-700 font-semibold">· ≈ {fmtSalePriceAmount(total, detail.roomType.salePriceCurrency)}{t('page.unit.month')} ({detail.roomType.area}m²)</span> : null;
                })()}
                {detail.roomType.salePriceNote && <Tip text={tr(detail.roomType.salePriceNote)}><span className="text-amber-600 text-xs cursor-help">ⓘ</span></Tip>}
              </div>
            )}
            {media.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[11px] text-[#8a7a68]">{t('page.detail.mediaCount', { n: media.length })}</div>
                <div className="grid grid-cols-2 gap-2">
                  {media.slice(0, 4).map((m, i) => (
                    <button key={m.id} type="button" onClick={() => setLightboxIndex(i)} className="relative">
                      {m.type === 'video'
                        ? <video src={m.url} className="w-full h-24 object-cover rounded-lg cursor-zoom-in bg-black" muted preload="metadata" />
                        : // eslint-disable-next-line @next/next/no-img-element
                          <img src={m.url} alt="" className="w-full h-24 object-cover rounded-lg cursor-zoom-in" />}
                      {m.type === 'video' && (
                        <span className="absolute inset-0 flex items-center justify-center text-white text-xl">▶</span>
                      )}
                      {i === 3 && media.length > 4 && (
                        <span className="absolute inset-0 rounded-lg bg-black/50 text-white text-sm font-semibold flex items-center justify-center">
                          +{media.length - 4}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {detail.roomType && (
              <div className="text-sm text-[#4a3d30] flex gap-4">
                <span>👤 {t('page.gallery.guests', { n: detail.roomType.maxAdults })}</span>
                {detail.roomType.area && <span>📐 {detail.roomType.area}m²</span>}
              </div>
            )}
            {amenityGroups.length > 0 && (
              <div className="space-y-2.5">
                {amenityGroups.map(g => (
                  <AmenityGroupChips key={g.label} group={g} />
                ))}
              </div>
            )}
            {booking && saleBookingEnabledFor(booking, detail?.buildingCode) && <SaleBookingPanel key={detail?.id ?? roomId} roomId={detail?.id ?? roomId} info={booking} contactPhone={contactPhone} searchRange={searchRange} />}
            <RoomReviews roomId={detail?.id ?? roomId} />
            <ViewingBookingForm roomId={detail?.id ?? roomId} />
            <button onClick={onClose} className="w-full py-2 rounded-xl bg-[#1c1a16] text-white text-sm font-medium">{t('page.detail.close')}</button>
          </div>
        )}
      </div>
      {lightboxIndex !== null && media.length > 0 && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-1000 bg-black/90 flex items-center justify-center p-4" onClick={e => { e.stopPropagation(); setLightboxIndex(null); }}>
          <button type="button" onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i === null ? null : (i - 1 + media.length) % media.length)); }}
            className="absolute left-2 sm:left-4 w-10 h-10 rounded-full bg-white/15 text-white text-xl flex items-center justify-center hover:bg-white/25">
            ‹
          </button>
          {media[lightboxIndex].type === 'video'
            ? <video src={media[lightboxIndex].url} controls className="max-w-full max-h-[80vh] rounded-xl" onClick={e => e.stopPropagation()} />
            : // eslint-disable-next-line @next/next/no-img-element
              <img src={media[lightboxIndex].url} alt="" className="max-w-full max-h-[80vh] rounded-xl object-contain cursor-zoom-out" onClick={e => { e.stopPropagation(); setLightboxIndex(null); }} />}
          <button type="button" onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i === null ? null : (i + 1) % media.length)); }}
            className="absolute right-2 sm:right-4 w-10 h-10 rounded-full bg-white/15 text-white text-xl flex items-center justify-center hover:bg-white/25">
            ›
          </button>
          <div className="absolute top-3 inset-x-0 flex items-center justify-center gap-2">
            <span className="px-3 py-1 rounded-full bg-white/15 text-white text-xs" style={MONO_FONT}>{lightboxIndex + 1} / {media.length}</span>
            {media[lightboxIndex].type === 'photo' && (
              <Tip text={t('page.detail.dlTip')}>
                <button type="button" disabled={downloading} onClick={e => { e.stopPropagation(); downloadPhoto(media[lightboxIndex].url, lightboxIndex); }}
                  className="px-3 py-1 rounded-full bg-white/15 text-white text-xs font-medium hover:bg-white/25 disabled:opacity-50">
                  {downloading ? t('page.detail.downloading') : t('page.detail.download')}
                </button>
              </Tip>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/** 1 nhóm tiện ích: mặc định tối đa 2 dòng badge (items đã xếp giá trị cao lên đầu), dư thì "Xem thêm" mở hết. */
function AmenityGroupChips({ group }: Readonly<{ group: { label: string; icon: string; items: string[] } }>) {
  const { t } = useSaleLang();
  const tr = useSaleTr([group.label, ...group.items]);
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(0); // số badge bị ẩn khi thu gọn (0 = vừa 2 dòng)

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return; // đang mở rộng thì giữ nút "Thu gọn", không đo
    // Badge nào có đỉnh nằm dưới ngưỡng 2 dòng (46px) = đang bị cắt → đếm để hiện "+N" trên nút
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      setHidden(Array.from(el.children).filter(c => c.getBoundingClientRect().top - top >= 46).length);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [group.items, expanded]);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="text-[11px] font-semibold text-[#8a7a68]">{group.icon} {tr(group.label)}</div>
        {(hidden > 0 || expanded) && (
          <button type="button" onClick={() => setExpanded(v => !v)} className="text-[11px] font-medium text-[#8a7a68] underline flex-none">
            {expanded ? t('page.amenity.collapse') : t('page.amenity.more', { n: hidden })}
          </button>
        )}
      </div>
      {/* 2 dòng chip cao 20px + gap 6px = 46px; chip dài bị cắt 1 dòng (truncate) khi thu gọn để không nở thành 2 dòng */}
      <div ref={ref} className={`flex flex-wrap gap-1.5 ${expanded ? '' : 'max-h-[46px] overflow-hidden'}`}>
        {group.items.map(a => (
          <span key={a} className={`px-2 py-0.5 rounded-full bg-[#f0ece5] text-[11px] leading-4 text-[#4a3d30] ${expanded ? '' : 'max-w-full truncate'}`}>{tr(a)}</span>
        ))}
      </div>
    </div>
  );
}

interface SaleCountry { code: string; name: string; flag: string; count: number }
interface SaleReview { name: string; date: string; textVi: string; textOriginal: string; country: { code: string; name: string; flag: string } | null }
interface SaleReviewSummary { points: { label: string; quote: string }[] }

/** Review 5★ của khách đã lưu trú — bản Việt, kèm bản gốc (gập) nếu khách viết ngôn ngữ khác. */
function RoomReviews({ roomId }: Readonly<{ roomId: string }>) {
  const { t, fd } = useSaleLang();
  const [reviews, setReviews] = useState<SaleReview[] | null>(null);
  const [countries, setCountries] = useState<SaleCountry[]>([]);
  const [summary, setSummary] = useState<SaleReviewSummary | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [openOrig, setOpenOrig] = useState<Set<number>>(new Set());

  useEffect(() => {
    setReviews(null); setCountries([]); setSummary(null); setShowAll(false); setOpenOrig(new Set());
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = (retry: boolean) => {
      fetch(`/api/sale/rooms/${roomId}/reviews`)
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(d => {
          if (cancelled) return;
          setReviews(Array.isArray(d.reviews) ? d.reviews : []);
          setCountries(Array.isArray(d.countries) ? d.countries : []);
          setSummary(d.summary && Array.isArray(d.summary.points) && d.summary.points.length > 0 ? d.summary : null);
          // Lần mở ĐẦU của căn: tóm tắt AI đang được tạo nền (~5-10s) → hỏi lại đúng 1 lần để hiện luôn
          if (d.summaryPending && retry) timer = setTimeout(() => load(false), 9000);
        })
        .catch(e => {
          if (cancelled) return;
          logPublicClientError({ route: 'app/sale/page.tsx', action: 'load_room_reviews', message: e instanceof Error ? e.message : String(e), details: { roomId } });
          if (retry) setReviews([]); // review chỉ là phần phụ — lỗi thì ẩn mục, không chặn modal
        });
    };
    load(true);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [roomId]);

  // Hook dịch PHẢI đứng trước return sớm bên dưới (quy tắc hooks). Chỉ dịch review đang hiện (3 đầu / tất cả)
  // + điểm khen AI + tên nước — nội dung dài, đừng dịch cả 20 review khi khách chưa bấm "Xem tất cả".
  const visible = showAll ? (reviews ?? []) : (reviews ?? []).slice(0, 3);
  const tr = useSaleTr([
    ...visible.map(r => r.textVi || r.textOriginal),
    ...(summary?.points ?? []).flatMap(p => [p.label, p.quote]),
    ...countries.slice(0, 12).map(c => c.name),
    ...visible.map(r => r.country?.name),
  ]);
  if (!reviews || reviews.length === 0) return null;
  const toggleOrig = (i: number) => setOpenOrig(s => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });

  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-[#1c1a16]">{t('page.review.title', { n: reviews.length })}</div>
      {countries.length > 0 && (
        <div className="text-xs text-[#8a7a68]">
          {renderBold(t('page.review.countries', { n: countries.length }))}{' '}
          {countries.slice(0, 12).map(c => (
            <Tip key={c.code} text={t('page.review.countryTip', { name: tr(c.name), count: c.count })}><span className="mr-0.5 cursor-help text-base align-middle">{c.flag}</span></Tip>
          ))}
        </div>
      )}
      {summary && (
        <div className="rounded-xl bg-amber-50 p-3 space-y-2 text-gray-800">
          <div className="text-[11px] font-semibold text-amber-800">{t('page.review.praise')}</div>
          {summary.points.map(p => (
            <div key={p.label}>
              <div className="text-sm font-medium text-[#1c1a16]">✓ {tr(p.label)}</div>
              <div className="text-xs italic text-[#8a7a68] pl-4">“{tr(p.quote)}”</div>
            </div>
          ))}
        </div>
      )}
      {visible.map((r, i) => (
        <div key={`${r.name}-${r.date}-${i}`} className="rounded-xl border border-[#e4ddd4] p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-semibold text-[#4a3d30]">{r.country && <Tip text={tr(r.country.name)}><span className="mr-1 cursor-help">{r.country.flag}</span></Tip>}{r.name} <span className="text-amber-500">★★★★★</span>{r.country && <span className="font-normal text-[#8a7a68]"> · {r.country.name}</span>}</span>
            {r.date && <span className="text-[#8a7a68]">{fd(r.date)}</span>}
          </div>
          <p className="text-sm text-[#4a3d30] whitespace-pre-line">{tr(r.textVi || r.textOriginal)}</p>
          {r.textVi && r.textOriginal && (
            <>
              <button type="button" onClick={() => toggleOrig(i)} className="text-[11px] text-[#8a7a68] underline">
                {openOrig.has(i) ? t('page.review.hideOrig') : t('page.review.showOrig')}
              </button>
              {openOrig.has(i) && <p className="text-xs text-[#8a7a68] whitespace-pre-line border-l-2 border-[#e4ddd4] pl-2">{r.textOriginal}</p>}
            </>
          )}
        </div>
      ))}
      {reviews.length > 3 && (
        <button type="button" onClick={() => setShowAll(v => !v)} className="text-xs font-medium text-[#4a3d30] underline">
          {showAll ? t('page.review.collapse') : t('page.review.showAll', { n: reviews.length })}
        </button>
      )}
    </div>
  );
}

function ViewingBookingForm({ roomId }: { roomId: string }) {
  const { t } = useSaleLang();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState('14:00');
  // Tên/SĐT khách đã nhập ở nơi khác (đặt phòng, gọi lại) → điền sẵn, hiện ra để sửa — không bắt nhập lại
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return <div className="p-3 rounded-xl bg-emerald-50 text-emerald-700 text-sm">{t('page.viewing.done')}</div>;
  }

  if (!open) {
    return (
      <button onClick={() => {
        // Đọc LÚC MỞ (không phải lúc mount): khách có thể vừa nhập SĐT ở khối đặt phòng/Gọi lại phía trên
        const sv = readSaleGuest();
        setName(n => n || sv.name || ''); setPhone(p => p || sv.phone || ''); setPrefilled(!!(sv.name || sv.phone));
        setOpen(true);
      }} className="w-full py-2 rounded-xl border border-brand-600 text-brand-600 text-sm font-medium">
        {t('page.viewing.open')}
      </button>
    );
  }

  const submit = async () => {
    if (!name.trim() || !phone.trim()) { setError(t('page.viewing.needFields')); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/sale/viewings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, scheduledDate: date, scheduledTime: time, contactName: name, contactPhone: phone }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error === 'invalid_phone' ? t('page.viewing.badPhone') : t('page.viewing.sendFail'));
        setSubmitting(false);
        return;
      }
      saveSaleGuest({ name, phone });
      setDone(true);
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'submit_viewing', message: e instanceof Error ? e.message : String(e) });
      setError(t('page.viewing.sendFailNet'));
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 rounded-xl bg-[#f8f5f1] space-y-2">
      <div className="text-sm font-semibold">{t('page.viewing.open')}</div>
      <div className="flex gap-2">
        <input type="date" value={date} min={todayISO()} onChange={e => setDate(e.target.value)} className="flex-1 border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm" />
        <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-28 border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm" />
      </div>
 {prefilled && <div className="text-[11px] text-[#8a7a68]">{t('page.viewing.prefilled')}</div>}
      <input value={name} onChange={e => setName(e.target.value)} placeholder={t('page.viewing.namePh')} className="w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm" />
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('page.viewing.phonePh')} className="w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm" />
      {error && <div className="text-xs text-rose-600">{error}</div>}
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="flex-1 py-2 rounded-lg border border-[#e4ddd4] text-sm">{t('page.viewing.cancel')}</button>
        <button onClick={submit} disabled={submitting} className="flex-1 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium disabled:opacity-50">
          {submitting ? t('page.viewing.sending') : t('page.viewing.send')}
        </button>
      </div>
    </div>
  );
}
